import { Injectable, InjectionToken, OnDestroy, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { EduContentBookInterface, EduContentBookQueries, ProductQueries, SchoolRoleMappingQueries } from '@campus/dal';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { UserJourney, UserJourneyContext, UserJourneyEvent, VariantType } from '../interfaces';
import { AB_TEST_CONFIG } from '../tokens/external.tokens';
import {
  DebugLogger,
  DeviceType,
  anonymizeUserId,
  createEventContext,
  createFunnelStepEvent,
  createJourneyEvent,
  detectDeviceType,
  generateEventId,
  generateJourneyId,
  getSessionId,
  isAnonymizedUserId,
  normalizeUrl,
} from '../utils';
import { ABTestAuthIntegrationService } from './ab-test-auth-integration.service';
import { EventBatcherService } from './event-batcher.service';

export interface UserJourneyConfig {
  autoFlushOnPageUnload?: boolean;
  autoFlushOnJourneyEnd?: boolean;
}

export interface UserJourneyTrackerServiceInterface {
  startJourney(userId: string, context: UserJourneyContext): void;
  endJourney(): void;
  trackJourneyEvent(event: UserJourneyEvent): void;
  trackPageNavigation(fromPage: string, toPage: string, loadTime?: number): void;
  trackPageView(
    page: string,
    loadTime?: number,
    routeSnapshot?: ActivatedRouteSnapshot | null,
    eventName?: string
  ): void;
  trackUserInteraction(
    action: string,
    elementId: string,
    properties?: Record<string, unknown>,
    eventName?: string
  ): void;
  trackFormInteraction(
    formId: string,
    action: string,
    elementId: string,
    properties?: Record<string, unknown>,
    eventName?: string
  ): void;
  trackFunnelStep(funnelId: string, step: string, eventName?: string): void;
  trackFunnelDropoff(funnelId: string, step: string, reason: string, eventName?: string): void;
  updateTestAssignments(testAssignments: Record<string, VariantType>): void;
  getCurrentJourney(): UserJourney | null;
}

export const AB_TEST_USER_JOURNEY_TRACKER = new InjectionToken<UserJourneyTrackerServiceInterface>(
  'UserJourneyTrackerService'
);

@Injectable({
  providedIn: 'root',
})
export class UserJourneyTrackerService implements OnDestroy {
  private currentJourney: UserJourney | null = null;
  private config: UserJourneyConfig = {
    autoFlushOnPageUnload: true,
    autoFlushOnJourneyEnd: true,
  };

  private destroy$ = new Subject<void>();
  private currentPageStartTime?: Date;

  private store = inject(Store);
  private eventBatcher = inject(EventBatcherService);
  private logger = inject(DebugLogger);
  private abTestConfig = inject(AB_TEST_CONFIG);
  private router = inject(Router);
  private authIntegrationService = inject(ABTestAuthIntegrationService);

  // Store reference to handler for clean removal
  private onPageUnloadHandler: () => void;
  private deviceType: DeviceType;

  // Store the current page normalized info for use in exit events
  private currentPageNormalized: {
    rawUrl: string;
    normalizedUrl: string;
    routeParams: Record<string, string>;
    queryParams: Record<string, string>;
  } | null = null;

  // Automatic page tracking state
  private navigationStartTime: number | null = null;

  private storeProducts = toSignal(this.store.select(ProductQueries.getAll), { initialValue: [] });
  private storeProductsByBookId = toSignal(this.store.select(ProductQueries.getProductsByBook), { initialValue: {} });
  private storeSchoolRoleMappingByPersonId = toSignal(this.store.select(SchoolRoleMappingQueries.getByPersonId), {
    initialValue: null,
  });
  private storeBooks = toSignal(this.store.select(EduContentBookQueries.getAllEntities), { initialValue: {} });

  constructor() {
    this.onPageUnloadHandler = this.onPageUnload.bind(this);
    this.deviceType = detectDeviceType();
    this.setupPageUnloadListeners();
  }

  /**
   * Start a new journey session
   * Session will continue until explicitly ended (logout, window close, or manual end)
   */
  startJourney(userId: string, context: UserJourneyContext = {}): void {
    this.authIntegrationService
      .isUserEligibleForTesting()
      .pipe(take(1))
      .subscribe((isEligible) => {
        if (!isEligible) return;

        // End current journey if exists
        if (this.currentJourney) this.endJourney();

        this.currentJourney = {
          journeyId: generateJourneyId(),
          sessionId: getSessionId(),
          userId,
          testAssignments: {},
          events: [],
          startTime: new Date(),
          context,
        };

        this.logger.log('Journey started:', {
          userId,
          journeyId: this.currentJourney.journeyId,
          sessionId: this.currentJourney.sessionId,
        });

        this.setupAutoPageTracking();
      });
  }

  /**
   * End the current journey
   */
  endJourney(): void {
    if (!this.currentJourney) {
      return;
    }

    this.currentJourney.endTime = new Date();

    if (this.config.autoFlushOnJourneyEnd) {
      this.sendJourneyCompletedEvent();
    }

    this.currentJourney = null;
  }

  /**
   * Track a journey event
   */
  trackJourneyEvent(event: UserJourneyEvent): void {
    if (!this.currentJourney) {
      return;
    }

    // Add test assignments to event properties
    const enrichedEvent: UserJourneyEvent = {
      ...event,
      properties: {
        ...event.properties,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.currentJourney.events.push(enrichedEvent);

    // Send event to EventBatcher in real-time
    this.sendJourneyEventToEventBatcher(enrichedEvent);
  }

  /**
   * Track page navigation
   */
  trackPageNavigation(fromPage: string, toPage: string, loadTime?: number): void {
    if (!this.currentJourney) {
      this.logger.warn('Page navigation tracked but no journey active', { fromPage, toPage });
      return;
    }

    const duration = this.currentPageStartTime ? Date.now() - this.currentPageStartTime.getTime() : undefined;

    const navigationEvent: UserJourneyEvent = {
      timestamp: new Date(),
      eventType: 'page_navigation',
      eventName: 'page_navigation',
      page: toPage,
      normalizedPage: this.getCurrentNormalizedPage(),
      action: 'navigate',
      userId: this.currentJourney.userId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      durationMs: duration,
      properties: {
        ...this.getParams(this.currentPageNormalized),
        fromPage,
        loadTime,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.logger.log('Page navigation:', { fromPage, toPage, duration, loadTime });

    this.trackJourneyEvent(navigationEvent);

    this.currentPageStartTime = new Date();
  }

  /**
   * Track page view
   * @param page - The URL path
   * @param loadTime - Optional page load time in milliseconds
   * @param routeSnapshot - Optional ActivatedRouteSnapshot for automatic route parameter extraction
   *                        If not provided, will be automatically retrieved from the Router
   * @param eventName - Optional custom event name (defaults to 'page_view')
   */
  trackPageView(
    page: string,
    loadTime?: number,
    routeSnapshot?: ActivatedRouteSnapshot | null,
    eventName: string = 'page_view'
  ): void {
    if (!this.currentJourney) {
      this.logger.warn('Page view tracked but no journey active', { page });
      return;
    }

    // If no route snapshot provided, get it from the router automatically
    const snapshot = routeSnapshot !== undefined ? routeSnapshot : this.getDeepestRouteSnapshot();

    // Normalize URL for analytics using Angular's route information
    const normalized = normalizeUrl(page, snapshot);

    const now = new Date();
    const previousPage = this.getCurrentPage();

    const gameCoins = this.getGameCoins(this.currentJourney.userId);

    if (gameCoins !== undefined && gameCoins !== null && this.currentJourney.context) {
      this.currentJourney.context.gameCoins = gameCoins;
    }

    // If there was a previous page, send a page_exit event with timing for THAT page
    if (this.currentPageStartTime && this.currentPageNormalized) {
      const timeOnPreviousPageMs = now.getTime() - this.currentPageStartTime.getTime();

      const params = this.getParams(this.currentPageNormalized);
      const bookId = this.getBookId(params);
      const productId: string = params['queryParams']?.product;
      let productInfo;
      let methodId;
      if (bookId !== undefined) {
        productInfo = this.getProductInfo(bookId, parseInt(productId, 10));
        methodId = this.getMethodId(bookId);
      }

      // Send page_exit event for the PREVIOUS page with its timing
      const pageExitEvent: UserJourneyEvent = {
        timestamp: now,
        eventType: 'page_exit',
        eventName: 'page_exit',
        page: previousPage, // The page user is LEAVING (raw URL)
        normalizedPage: this.currentPageNormalized.normalizedUrl,
        action: 'exit',
        userId: this.currentJourney.userId,
        sessionId: this.currentJourney.sessionId,
        journeyId: this.currentJourney.journeyId,
        durationMs: timeOnPreviousPageMs,
        properties: {
          ...params,
          methodId,
          bookId,
          ...(productInfo && {
            isMarketingHighlighted: productInfo.isMarketingHighlighted,
            hasOnlyMarketingLicenses: productInfo.hasOnlyMarketingLicenses,
          }),
          userContext: this.currentJourney.context,
          nextPage: page, // Where they're going
          testAssignments: this.currentJourney.testAssignments,
        },
      };

      this.logger.log('Page exit:', {
        page: previousPage,
        normalizedPage: this.currentPageNormalized.normalizedUrl,
        duration: timeOnPreviousPageMs,
        nextPage: page,
      });

      this.trackJourneyEvent(pageExitEvent);
    }

    const params = this.getParams(normalized);
    const bookId = this.getBookId(params);

    const productId: string = params['queryParams']?.product;

    let productInfo;
    let methodId;
    if (bookId !== undefined) {
      productInfo = this.getProductInfo(bookId, parseInt(productId, 10));
      methodId = this.getMethodId(bookId);
    }

    // Send page_view event for the NEW page (no timing, just marks entry)
    const pageViewEvent: UserJourneyEvent = {
      timestamp: now,
      eventType: 'page_view',
      eventName: eventName,
      page, // Keep raw URL in page field
      normalizedPage: normalized.normalizedUrl,
      action: 'view',
      userId: this.currentJourney.userId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      properties: {
        ...params,
        methodId,
        bookId,
        ...(productInfo && {
          isMarketingHighlighted: productInfo.isMarketingHighlighted,
          hasOnlyMarketingLicenses: productInfo.hasOnlyMarketingLicenses,
        }),
        userContext: this.currentJourney.context,
        previousPage,
        loadTime,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.logger.log('Page view:', {
      page,
      normalizedPage: normalized.normalizedUrl,
      routeParams: normalized.routeParams,
      loadTime,
    });
    this.trackJourneyEvent(pageViewEvent);

    // Store normalized info for use in the next page_exit event
    this.currentPageNormalized = {
      rawUrl: normalized.rawUrl,
      normalizedUrl: normalized.normalizedUrl,
      routeParams: normalized.routeParams,
      queryParams: normalized.queryParams,
    };
    this.currentPageStartTime = now;
  }

  /**
   * Track user interaction
   * @param action - The action performed
   * @param elementId - The element identifier
   * @param properties - Additional properties
   * @param eventName - Optional custom event name (defaults to 'interaction')
   */
  trackUserInteraction(
    action: string,
    elementId: string,
    properties?: Record<string, unknown>,
    eventName: string = 'interaction'
  ): void {
    if (!this.currentJourney) {
      this.logger.warn('User interaction tracked but no journey active', { action, elementId });
      return;
    }

    const params = this.getParams(this.currentPageNormalized);
    const bookId = this.getBookId(params);
    const productId: string = params['queryParams']?.product;
    let productInfo;
    let methodId;
    if (bookId !== undefined) {
      productInfo = this.getProductInfo(bookId, parseInt(productId, 10));
      methodId = this.getMethodId(bookId);
    }

    const gameCoins = this.getGameCoins(this.currentJourney.userId);
    if (gameCoins !== undefined && gameCoins !== null && this.currentJourney.context) {
      this.currentJourney.context.gameCoins = gameCoins;
    }

    const interactionEvent: UserJourneyEvent = {
      timestamp: new Date(),
      eventType: 'interaction',
      eventName: eventName,
      page: this.getCurrentPage(),
      normalizedPage: this.getCurrentNormalizedPage(),
      action,
      userId: this.currentJourney.userId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      elementId,
      properties: {
        ...params,
        methodId,
        bookId,
        ...properties,
        ...(productInfo && {
          isMarketingHighlighted: productInfo.isMarketingHighlighted,
          hasOnlyMarketingLicenses: productInfo.hasOnlyMarketingLicenses,
        }),
        userContext: this.currentJourney.context,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.logger.log('User interaction:', {
      action,
      elementId,
      properties,
      eventName,
    });

    this.trackJourneyEvent(interactionEvent);
  }

  /**
   * Track form interaction
   * @param formId - The form identifier
   * @param action - The action performed
   * @param elementId - The element identifier
   * @param properties - Additional properties
   * @param eventName - Optional custom event name (defaults to 'form_interaction')
   */
  trackFormInteraction(
    formId: string,
    action: string,
    elementId: string,
    properties?: Record<string, unknown>,
    eventName: string = 'form_interaction'
  ): void {
    if (!this.currentJourney) {
      return;
    }

    const formEvent: UserJourneyEvent = {
      timestamp: new Date(),
      eventType: 'form_interaction',
      eventName: eventName,
      page: this.getCurrentPage(),
      normalizedPage: this.getCurrentNormalizedPage(),
      action,
      userId: this.currentJourney.userId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      elementId,
      properties: {
        ...this.getParams(this.currentPageNormalized),
        formId,
        ...properties,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.trackJourneyEvent(formEvent);
  }

  /**
   * Track funnel step
   * @param funnelId - The funnel identifier
   * @param step - The step name
   * @param eventName - Optional custom event name (defaults to 'funnel_step')
   */
  trackFunnelStep(funnelId: string, step: string, eventName: string = 'funnel_step'): void {
    if (!this.currentJourney) {
      return;
    }

    const now = new Date();
    const funnelEvents = this.currentJourney.events.filter(
      (e) => e.eventType === 'funnel_step' && e.properties?.['funnelId'] === funnelId
    );

    const firstFunnelEvent = funnelEvents[0];
    const lastFunnelEvent = funnelEvents[funnelEvents.length - 1];

    const timeToConversion = firstFunnelEvent ? now.getTime() - firstFunnelEvent.timestamp.getTime() : 0;

    const durationSincePreviousStep = lastFunnelEvent ? now.getTime() - lastFunnelEvent.timestamp.getTime() : undefined;

    const funnelEvent: UserJourneyEvent = {
      timestamp: now,
      eventType: 'funnel_step',
      eventName: eventName,
      page: this.getCurrentPage(),
      normalizedPage: this.getCurrentNormalizedPage(),
      action: 'step_completed',
      userId: this.currentJourney.userId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      properties: {
        ...this.getParams(this.currentPageNormalized),
        funnelId,
        step,
        durationSincePreviousStep,
        timeToConversion,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.trackJourneyEvent(funnelEvent);
  }

  /**
   * Track funnel dropoff
   * @param funnelId - The funnel identifier
   * @param step - The step where dropoff occurred
   * @param reason - The reason for dropoff
   * @param eventName - Optional custom event name (defaults to 'funnel_dropoff')
   */
  trackFunnelDropoff(funnelId: string, step: string, reason: string, eventName: string = 'funnel_dropoff'): void {
    if (!this.currentJourney) {
      return;
    }

    const dropoffEvent: UserJourneyEvent = {
      timestamp: new Date(),
      eventType: 'funnel_dropoff',
      eventName: eventName,
      page: this.getCurrentPage(),
      normalizedPage: this.getCurrentNormalizedPage(),
      action: 'dropoff',
      userId: this.currentJourney.userId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      properties: {
        ...this.getParams(this.currentPageNormalized),
        funnelId,
        step,
        reason,
        testAssignments: this.currentJourney.testAssignments,
      },
    };

    this.trackJourneyEvent(dropoffEvent);
  }

  /**
   * Update test assignments for current journey
   */
  updateTestAssignments(testAssignments: Record<string, VariantType>): void {
    if (!this.currentJourney) {
      return;
    }

    this.currentJourney.testAssignments = { ...testAssignments };
  }

  /**
   * Get current journey
   */
  getCurrentJourney(): UserJourney | null {
    return this.currentJourney;
  }

  /**
   * Configure behavior
   */
  configureBehavior(config: UserJourneyConfig): void {
    if (config.autoFlushOnPageUnload !== undefined) {
      this.config.autoFlushOnPageUnload = config.autoFlushOnPageUnload;
    }
    if (config.autoFlushOnJourneyEnd !== undefined) {
      this.config.autoFlushOnJourneyEnd = config.autoFlushOnJourneyEnd;
    }
  }

  /**
   * Get current configuration
   */
  getConfiguration(): UserJourneyConfig {
    return { ...this.config };
  }

  /**
   * Setup page unload listeners to track final page_exit event
   */
  private setupPageUnloadListeners(): void {
    if (typeof window === 'undefined') return;

    // Listen for page unload to create final page_exit event and flush
    window.addEventListener('beforeunload', this.onPageUnloadHandler);
    window.addEventListener('pagehide', this.onPageUnloadHandler);
  }

  /**
   * Setup automatic page view tracking if enabled in configuration
   */
  private setupAutoPageTracking(): void {
    if (!this.abTestConfig.autoTrackPageViews) {
      return;
    }

    // Track navigation start time for load performance
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.navigationStartTime = performance.now();
      });

    // Track page views with load time on navigation end
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const loadTime = this.navigationStartTime
          ? Math.round(performance.now() - this.navigationStartTime)
          : undefined;

        this.trackPageView(event.urlAfterRedirects, loadTime);
        this.navigationStartTime = null;
      });
  }

  /**
   * Clean up expired journeys
   * Note: Journeys are no longer automatically expired based on duration.
   * They must be explicitly ended via logout, window close, or manual endJourney() call.
   */
  cleanupExpiredJourneys(): void {
    // No-op: Journeys persist until explicitly ended
    return;
  }

  /**
   * Handle page unload (window close, tab close, navigation away)
   * Creates final page_exit event and triggers flush of all pending events
   */
  onPageUnload(): void {
    if (!this.currentJourney) {
      return;
    }

    // Create final page_exit event for current page
    if (this.currentPageStartTime) {
      const now = new Date();
      const currentPage = this.getCurrentPage();
      const timeOnPageMs = now.getTime() - this.currentPageStartTime.getTime();

      // Create and track page_exit event (adds to event batcher queue)
      const pageExitEvent: UserJourneyEvent = {
        timestamp: now,
        eventType: 'page_exit',
        eventName: 'page_exit',
        page: currentPage,
        normalizedPage: this.currentPageNormalized?.normalizedUrl || '',
        action: 'unload',
        userId: this.currentJourney.userId,
        sessionId: this.currentJourney.sessionId,
        journeyId: this.currentJourney.journeyId,
        durationMs: timeOnPageMs,
        properties: {
          exitReason: 'page_unload',
          routeParams: this.currentPageNormalized?.routeParams,
          userContext: this.currentJourney.context,
          testAssignments: this.currentJourney.testAssignments,
        },
      };

      this.trackJourneyEvent(pageExitEvent);
    }

    // Force flush all queued events immediately (including the page_exit event just created)
    if (this.config.autoFlushOnPageUnload) {
      this.eventBatcher.forceFlush();
    }
  }

  /**
   * Get current page from latest page event
   */
  private getCurrentPage(): string {
    if (!this.currentJourney) {
      return '';
    }

    const pageEvents = this.currentJourney.events.filter(
      (e) => e.eventType === 'page_view' || e.eventType === 'page_navigation'
    );

    return pageEvents.length > 0 ? pageEvents[pageEvents.length - 1].page : '';
  }

  /**
   * Get current normalized page
   */
  private getCurrentNormalizedPage(): string {
    return this.currentPageNormalized?.normalizedUrl || '';
  }

  /**
   * Get the deepest activated route snapshot (the final child route)
   * This gives us the most specific route parameters
   */
  private getDeepestRouteSnapshot(): ActivatedRouteSnapshot | null {
    if (!this.router.routerState?.root) {
      return null;
    }

    let snapshot: ActivatedRouteSnapshot = this.router.routerState.root.snapshot;
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return snapshot;
  }

  /**
   * Send individual journey event to event batcher
   */
  private sendJourneyEventToEventBatcher(journeyEvent: UserJourneyEvent): void {
    if (!this.currentJourney) {
      return;
    }

    const anonymizedUserId = this.anonymizeUserIdIfEnabled(this.currentJourney.userId);

    // Handle funnel_step events differently using createFunnelStepEvent
    if (journeyEvent.eventType === 'funnel_step') {
      const abTestEvent = createFunnelStepEvent({
        eventId: generateEventId(),
        userId: anonymizedUserId,
        sessionId: this.currentJourney.sessionId,
        journeyId: this.currentJourney.journeyId,
        funnelId: journeyEvent.properties?.['funnelId'] as string,
        funnelStep: journeyEvent.properties?.['step'] as string,
        page: journeyEvent.page,
        normalizedPage: journeyEvent.normalizedPage,
        durationSincePreviousStep: journeyEvent.properties?.['durationSincePreviousStep'] as number | undefined,
        timeToConversionMs: journeyEvent.properties?.['timeToConversion'] as number | undefined,
        timestamp: journeyEvent.timestamp,
        context: createEventContext(this.deviceType),
        additionalProperties: {
          testAssignments: this.currentJourney.testAssignments,
        },
      });

      this.eventBatcher.addEvent(abTestEvent);
      return;
    }

    // Handle all other journey events using createJourneyEvent
    const abTestEvent = createJourneyEvent({
      eventId: generateEventId(),
      userId: anonymizedUserId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      eventName: journeyEvent.eventType,
      page: journeyEvent.page,
      normalizedPage: journeyEvent.normalizedPage,
      action: journeyEvent.action,
      elementId: journeyEvent.elementId,
      durationMs: journeyEvent.durationMs,
      pageLoadTimeMs: journeyEvent.properties?.['loadTime'] as number | undefined,
      timestamp: journeyEvent.timestamp,
      context: createEventContext(this.deviceType),
      additionalProperties: {
        ...journeyEvent.properties,
        testAssignments: this.currentJourney.testAssignments,
      },
    });

    this.eventBatcher.addEvent(abTestEvent);
  }

  /**
   * Send journey completed event to event batcher
   */
  private sendJourneyCompletedEvent(): void {
    if (!this.currentJourney) {
      return;
    }

    const anonymizedUserId = this.anonymizeUserIdIfEnabled(this.currentJourney.userId);

    const journeyEvent = createJourneyEvent({
      eventId: generateEventId(),
      userId: anonymizedUserId,
      sessionId: this.currentJourney.sessionId,
      journeyId: this.currentJourney.journeyId,
      eventName: 'journey_completed',
      page: this.getCurrentPage(),
      action: 'completed',
      timestamp: new Date(),
      context: createEventContext(this.deviceType),
      additionalProperties: {
        testAssignments: this.currentJourney.testAssignments,
      },
    });

    this.eventBatcher.addEvent(journeyEvent);
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    if (this.currentJourney) {
      this.endJourney();
    }

    this.cleanupEventListeners();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Clean up event listeners
   */
  private cleanupEventListeners(): void {
    if (typeof window === 'undefined') return;

    // Remove page unload listeners
    window.removeEventListener('beforeunload', this.onPageUnloadHandler);
    window.removeEventListener('pagehide', this.onPageUnloadHandler);
  }

  /**
   * Anonymizes a user ID if anonymization is enabled in configuration
   * Returns the anonymized ID if enabled, otherwise returns the original ID
   */
  private anonymizeUserIdIfEnabled(userId: string): string {
    const shouldAnonymize = this.abTestConfig.anonymizeData;
    this.logger.log(`Anonymization check: shouldAnonymize=${shouldAnonymize}, userId=${userId}`);

    if (!shouldAnonymize) {
      return userId;
    }

    // Don't double-anonymize
    if (isAnonymizedUserId(userId)) {
      this.logger.log(`User ID already anonymized: ${userId}`);
      return userId;
    }

    const anonymized = anonymizeUserId(userId);
    this.logger.log(`Anonymized ${userId} -> ${anonymized}`);
    return anonymized;
  }

  private getParams(
    normalized: {
      rawUrl: string;
      normalizedUrl: string;
      routeParams: Record<string, string>;
      queryParams: Record<string, string>;
    } | null
  ): Record<string, any> {
    const params = {
      ...(normalized?.routeParams &&
        Object.keys(normalized?.routeParams).length && {
          routeParams: normalized.routeParams,
        }),
      ...(normalized?.queryParams &&
        Object.keys(normalized?.queryParams).length && {
          queryParams: normalized.queryParams,
        }),
    };

    return params;
  }

  /**
   * Retrieves product information for a specific bookId and productId.
   *
   * `isMarketingHighlighted`
   * Indicates if the product is marketing highlighted
   *
   * `hasOnlyMarketingLicenses`
   * Indicates if the book is only accessible because of a marketing highlighted product.
   * If this is `true` this indicates that the book is visible because of a marketing highlighted product.
   * If `false` then this indicates that the user already has access to the product
   */
  private getProductInfo(
    bookId: number,
    productId: number
  ): { isMarketingHighlighted?: boolean; hasOnlyMarketingLicenses?: boolean } | undefined {
    if (!bookId) return;

    const productsForBook = this.storeProductsByBookId()[bookId];
    if (!productsForBook?.length) return;

    const hasOnlyMarketingLicenses = productsForBook.every((product) => product.isMarketingHighlight);

    if (!productId) return { hasOnlyMarketingLicenses };

    const products = this.storeProducts();
    const currentProduct = products.find((product) => product.id === productId);
    if (!currentProduct) return { hasOnlyMarketingLicenses };

    const isMarketingHighlighted = !!currentProduct.isMarketingHighlight;
    return { isMarketingHighlighted, hasOnlyMarketingLicenses };
  }

  private getGameCoins(userId: string): number | undefined {
    const schoolRoleMappingByPersonId = this.storeSchoolRoleMappingByPersonId();
    if (!schoolRoleMappingByPersonId) return;

    const userSchoolRoleMapping = schoolRoleMappingByPersonId[userId]?.[0];
    if (!userSchoolRoleMapping) return;

    return userSchoolRoleMapping.gameCoins;
  }

  private getMethodId(bookId: number): number | undefined {
    const books = this.storeBooks() as Record<string, EduContentBookInterface>;
    return books[bookId]?.methodId;
  }

  private getBookId(params: Record<string, any>): number | undefined {
    const bookId =
      params['routeParams']?.book ||
      params['queryParams']?.book ||
      params['routeParams']?.bookId ||
      params['queryParams']?.bookId;

    if (!bookId) return;

    const parsedBookId = parseInt(bookId, 10);
    return isNaN(parsedBookId) ? undefined : parsedBookId;
  }
}
